import React, { useState, useMemo, useEffect } from 'react';
import { Modal, List, Input, Select, Avatar } from 'antd';
import styles from './ImageSelectModal.module.css';
import { Image } from '../../types/types';
import type { ImageCategory } from '../../types/types';
import { useStore } from '../../hooks/useStore';
import { useStableCallback } from '../../hooks/hookUtils';
const { Search } = Input;
const { Option } = Select;

interface ImageSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (imageId: number) => void;
  images: Image[];
}

const ImageSelectModal: React.FC<ImageSelectModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  images,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const imageCategories = useStore(state => state.session?.cache?.imageCategories);

  const filteredImages = useMemo(() => {
    const lowerCaseSearch = searchTerm.toLowerCase();
    return (images ?? [])
      .filter(img => {
        const nameMatch = img.name.toLowerCase().includes(lowerCaseSearch);
        const categoryMatch = !categoryFilter || img.categoryId === Number(categoryFilter);
        return nameMatch && categoryMatch;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [searchTerm, categoryFilter, images]);

  // useStableCallback ensures stable reference for handleSelect, preventing
  // unnecessary re-renders of the List component that receives this callback
  const handleSelect = useStableCallback((image: Image) => {
    onSelect(image.id);
    onClose();
  });

  // Reset filters when modal is closed
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      setCategoryFilter('');
    }
  }, [isOpen]);

  return (
    <Modal
      title="Select an Image"
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={800}
      destroyOnHidden
    >
      <div className={styles.filters}>
        <Search
          placeholder="Search images..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          allowClear
        />
        <Select
          value={categoryFilter}
          onChange={setCategoryFilter}
          placeholder="All Categories"
          allowClear
        >
          {(imageCategories ?? []).map((cat: ImageCategory) => <Option key={cat.id} value={String(cat.id)}>{cat.name}</Option>)}
        </Select>
      </div>
      <List
        className={styles.list}
        grid={{ gutter: 16, xs: 2, sm: 3, md: 4, lg: 5, xl: 6 }}
        dataSource={filteredImages}
        renderItem={image => (
          <List.Item>
            <div onClick={() => handleSelect(image)} style={{ cursor: 'pointer' }}>
              <Avatar shape="square" size={100} src={image.imageUrl} />
              <div className={styles.name}>
                {image.name}
              </div>
            </div>
          </List.Item>
        )}
      />
    </Modal>
  );
};

export default ImageSelectModal;